import React, { useState } from 'react';

import type { Column } from 'api/shared/models';
import { calculateColumn } from 'components/shared/CustomTable/helpers';

import type { Pagination } from 'commons/modules/pagination';

import useTheme from './useTheme';

const paginationDefault = {
  page: 1,
  pageSize: 50,
  total: 0,
};

function useTable<T>() {
  const { palette, isThemeDark } = useTheme();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [pagination, setPagination] = useState<Pagination>(paginationDefault);
  const [rows, setRows] = useState<T[]>([]);

  const getStylesByColumn = (
    index: number,
    column: Column,
    columns: Column[],
  ): React.CSSProperties | undefined => {
    if (column.fixedColumn) {
      const minnWidth = column.width ? Number(column.width) + 8 : 'auto';
      let backgroundColor =
        index % 2 === 0 ? palette.blue[600] : palette.background.paper;
      if (isThemeDark) {
        backgroundColor = index % 2 === 0 ? '#1E232B' : '#1c2424';
      }

      return {
        backgroundColor,
        left: calculateColumn(columns, column.apiField),
        minWidth: minnWidth,
        width: minnWidth,
      };
    }
    return undefined;
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    value: number,
  ) => {
    setPagination({ ...pagination, page: value + 1 });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<any>) => {
    setPagination({
      ...pagination,
      page: 1,
      pageSize: event.target.value,
    });
  };

  const cleanTable = () => {
    setLoading(false);
    setError('');
    setPagination(paginationDefault);
    setRows([]);
  };

  const startService = () => {
    setLoading(true);
    setError('');
    setRows([]);
  };

  return {
    loading,
    error,
    pagination,
    rows,
    setLoading,
    setError,
    setPagination,
    setRows,
    cleanTable,
    getStylesByColumn,
    startService,
    paginationDefault,
    handleChangePage,
    handleChangeRowsPerPage,
  };
}

export default useTable;
